import React, { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiShoppingCart, FiTruck, FiFileText, FiBox, FiDatabase, FiUsers, FiShield, FiSettings, FiActivity, FiBarChart2, FiDownload, FiDollarSign, FiPackage, FiArchive, FiKey } from 'react-icons/fi';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTranslation } from 'react-i18next';

interface MenuItem {
  to: string;
  icon: React.ReactNode;
  labelKey: string;
  module: string;
  requireWrite?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { hasRead, hasWrite } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const mainMenu: MenuItem[] = [
    { to: '/', icon: <FiHome />, labelKey: 'nav.dashboard', module: 'Dashboard' },
    { to: '/purchase-orders', icon: <FiShoppingCart />, labelKey: 'nav.purchaseOrders', module: 'PurchaseOrders' },
    // Vendor ASN workflow — hidden: Krosno uses FBA only, not merchant-fulfilled shipping.
    // Route + code preserved in case Vendor Central access is granted later.
    // { to: '/shipments', icon: <FiTruck />, labelKey: 'nav.shipments', module: 'Shipments' },
    { to: '/fba-fulfilled', icon: <FiPackage />, labelKey: 'nav.fbaFulfilled', module: 'Shipments' },
    { to: '/fba-inbound', icon: <FiArchive />, labelKey: 'nav.fbaInbound', module: 'Shipments' },
    { to: '/invoices', icon: <FiFileText />, labelKey: 'nav.invoices', module: 'Invoices' },
    { to: '/amazon-invoices', icon: <FiDownload />, labelKey: 'nav.amazonInvoices', module: 'Invoices' },
    { to: '/customer-invoices', icon: <FiFileText />, labelKey: 'nav.customerInvoices', module: 'Invoices' },
    { to: '/settlements', icon: <FiDollarSign />, labelKey: 'nav.settlements', module: 'Invoices' },
  ];

  const catalogMenu: MenuItem[] = [
    { to: '/products', icon: <FiBox />, labelKey: 'nav.products', module: 'Products' },
    { to: '/inventory', icon: <FiDatabase />, labelKey: 'nav.inventory', module: 'Inventory' },
    { to: '/reports', icon: <FiBarChart2 />, labelKey: 'nav.reports', module: 'Dashboard' },
  ];

  const adminMenu: MenuItem[] = [
    { to: '/users', icon: <FiUsers />, labelKey: 'nav.users', module: 'Users' },
    { to: '/permissions', icon: <FiShield />, labelKey: 'nav.permissions', module: 'Users', requireWrite: true },
    { to: '/settings/company', icon: <FiSettings />, labelKey: 'nav.companyProfile', module: 'Invoices', requireWrite: true },
    { to: '/settings/amazon-auth', icon: <FiKey />, labelKey: 'nav.amazonAuth', module: 'Sync', requireWrite: true },
    { to: '/sync-logs', icon: <FiActivity />, labelKey: 'nav.syncLogs', module: 'Dashboard' },
  ];

  const handleLinkClick = useCallback(
    (to: string) => {
      navigate(to);
      onClose();
    },
    [navigate, onClose]
  );

  const renderLink = (item: MenuItem) => {
    if (item.requireWrite ? !hasWrite(item.module as any) : !hasRead(item.module as any)) {
      return null;
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          handleLinkClick(item.to);
        }}
      >
        <span className="icon">{item.icon}</span>
        <span className="sidebar-link-label">{t(item.labelKey)}</span>
      </NavLink>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">AK</div>
        <div className="sidebar-header-text">
          <div className="sidebar-title">AmazonKrosno</div>
          <div className="sidebar-subtitle">{t('nav.subtitle')}</div>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <div className="sidebar-section-title">{t('nav.menu')}</div>
          {mainMenu.map(renderLink)}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-title">{t('nav.catalog')}</div>
          {catalogMenu.map(renderLink)}
        </div>

        {(hasRead('Users' as any)) && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">{t('nav.administration')}</div>
            {adminMenu.map(renderLink)}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
